import type { ClientNutritionPlan, DayPlan, Meal, DayType } from '../types/nutrition';
import { computeMealTotals, computeDayTotals } from '../utils/nutritionMath';

export function selectClientPlan(state: any, clientId: string): ClientNutritionPlan | null {
  const plans: ClientNutritionPlan[] = Array.isArray(state?.clientNutritionPlans) ? state.clientNutritionPlans : [];
  const plan = plans.find((p) => String(p?.clientId) === String(clientId)) || null;
  return plan;
}

export function selectPlanDayTypes(plan: ClientNutritionPlan | null): DayType[] {
  if (!plan) return [] as DayType[];
  // View-only: return base snapshot dayTypes without overlays
  return (plan.planData?.dayTypes || []) as DayType[];
}

export function selectDayTypeById(plan: ClientNutritionPlan | null, dayTypeId?: string): DayType | null {
  if (!plan || !dayTypeId) return null;
  const merged = selectPlanDayTypes(plan);
  const byId = merged.find((dt: any) => String(dt.id) === String(dayTypeId));
  if (byId) return byId as DayType;
  // Fallback: match by name if caller passed name as id
  const byName = merged.find((dt: any) => String(dt.name || '').toLowerCase() === String(dayTypeId).toLowerCase());
  return (byName as any) ?? null;
}

export function computeDayTypeTotals(dt?: DayType) {
  if (!dt) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
  return (dt.meals || []).reduce(
    (acc, m) => {
      const t = computeMealTotals(m as any);
      return {
        calories: acc.calories + t.calories,
        protein: acc.protein + t.protein,
        carbs: acc.carbs + t.carbs,
        fat: acc.fat + t.fat,
      };
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0 },
  );
}

export function selectMealsForDate(plan: ClientNutritionPlan | null | undefined, dateISO: string): Meal[] {
  if (!plan || !dateISO) return [];
  const days: DayPlan[] = Array.isArray(plan.planData?.days) ? plan.planData.days : [];
  const day = days.find((d) => d.date === dateISO) || null;
  return day?.meals || [];
}

export function selectDailyTotals(plan: ClientNutritionPlan | null | undefined, dateISO: string) {
  const meals = selectMealsForDate(plan, dateISO);
  if (!meals.length) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
  const d: DayPlan = {
    id: 'x',
    dayNumber: 0,
    dayName: '',
    meals,
    dailyNutrition: { calories: 0, protein: 0, carbs: 0, fat: 0 },
  } as any;
  return computeDayTotals(d);
}

export function selectWeeklyTotals(plan: ClientNutritionPlan | null | undefined, weekStartISO: string) {
  if (!plan || !weekStartISO) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
  const days: DayPlan[] = Array.isArray(plan.planData?.days) ? plan.planData.days : [];
  const start = new Date(weekStartISO + 'T00:00:00');
  const totals = { calories: 0, protein: 0, carbs: 0, fat: 0 };
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const meals = days.find((x) => x.date === iso)?.meals || [];
    const t = computeDayTotals({
      id: 'x',
      dayNumber: 0,
      dayName: '',
      meals,
      dailyNutrition: { calories: 0, protein: 0, carbs: 0, fat: 0 },
    } as any);
    totals.calories += t.calories;
    totals.protein += t.protein;
    totals.carbs += t.carbs;
    totals.fat += t.fat;
  }
  return totals;
}

export function selectTargetMacros(plan: ClientNutritionPlan | null | undefined) {
  const t = plan?.planData?.targetNutrition;
  return {
    targetCalories: t?.dailyCalories ?? 0,
    proteinPct: t?.proteinPercentage ?? 0,
    carbsPct: t?.carbsPercentage ?? 0,
    fatPct: t?.fatPercentage ?? 0,
  };
}
